import { useEffectOnce } from '@talos/kyoko';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPreTradeRoute } from '../../../../containers/Routes/routes';
import { useAppStateDispatch, useAppStateSelector } from '../../../../providers/AppStateProvider';
import type { PreTradePanelConfig } from '../../../../types/LayoutConfig';
import { Panel } from '../../../Layout/Components/Panel';
import { PreTradeDataBoundary } from '../../../PreTrade/components/PreTradeDataBoundary';
import { clearActiveSessionIfMatches } from '../../../PreTrade/redux/preTradeSlice';
import { useIsActivePreTradeSession } from '../../../PreTrade/sessionAccess';
import { type PreTradeTabType, resolvePreTradeRequestId } from '../../../PreTrade/types';
import { selectCurrentLayout } from '../../AppLayoutSlice';
import { DEFAULT_LAYOUT_ID } from '../../Layouts/tokens';
import { TEMPORARY_ID } from '../../tokens';
import type { LayoutPanelProps } from '../../types';

export function PreTradePanel({ params, api }: LayoutPanelProps<PreTradePanelConfig>) {
  const dispatch = useAppStateDispatch();
  const navigate = useNavigate();
  const currentLayout = useAppStateSelector(selectCurrentLayout);
  const sessionId = params.sessionId;
  const isTemporaryPanel = api.id === TEMPORARY_ID;
  const isActiveSession = useIsActivePreTradeSession({ sessionId, panelId: api.id });

  useEffectOnce(() => {
    // temporary tabs may be active only while mounted (clear on unmount).
    if (isTemporaryPanel) {
      return () => {
        dispatch(clearActiveSessionIfMatches({ sessionId }));
      };
    }

    // Permanent tabs represent archived sessions (clear immediately);
    dispatch(clearActiveSessionIfMatches({ sessionId }));
  });

  // Keeps panel state and the browser URL in sync when switching tabs.
  const handleTabChange = useCallback(
    (openTab: PreTradeTabType, requestId?: string) => {
      const resolvedRequestId = resolvePreTradeRequestId(openTab, requestId);
      api.updateParameters({
        ...params,
        openTab,
        requestId: resolvedRequestId,
      });

      // Temporary panels don't have a persistent route — navigating would trigger panel recreation.
      if (isTemporaryPanel) {
        return;
      }

      // Replace history (not push) so tab switches don't pollute the back stack.
      navigate(
        getPreTradeRoute({
          layoutId: currentLayout?.id ?? DEFAULT_LAYOUT_ID,
          sessionId,
          id: api.id,
          tab: openTab,
          requestId: resolvedRequestId,
        }),
        { replace: true }
      );
    },
    [api, currentLayout?.id, isTemporaryPanel, navigate, params, sessionId]
  );

  return (
    <Panel panelApi={api} data-testid="pre-trade-panel">
      <PreTradeDataBoundary
        key={sessionId}
        sessionId={sessionId}
        isActiveSession={isActiveSession}
        openTab={params.openTab}
        requestId={params.requestId}
        onTabChange={handleTabChange}
      />
    </Panel>
  );
}
